import React, { useState } from 'react';
import { Plus, Edit, Trash2, BadgeCheck } from 'lucide-react';
import { useCrud } from '../../hooks/useApi';
import { institutionService } from '../../services/api';
import EmptyState from '../../components/Common/EmptyState';
import FormModalLayout from '../../components/Common/FormModalLayout';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import Can from '../../components/Common/Can';
import { getAppHomePath } from '../../constants/navigation';
import { P } from '../../constants/permissions';
import { useTranslation } from 'react-i18next';

const Cargos = () => {
  const { t } = useTranslation('institution');

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filters, setFilters] = useState({ search: '' });

  const { useList, useCreate, useUpdate, useDelete } = useCrud('cargos', institutionService.cargoCrud);
  const { data, isLoading, error } = useList();
  const list = Array.isArray(data) ? data : data?.data ?? [];
  const createMutation = useCreate();
  const updateMutation = useUpdate();
  const deleteMutation = useDelete();

  const filtered = list.filter((c) => !filters.search || (c.name && c.name.toLowerCase().includes(filters.search.toLowerCase())));

  const handleSubmit = (formData) => {
    if (editing) updateMutation.mutate({ id: editing.id, data: formData });
    else createMutation.mutate(formData);
    setShowModal(false);
    setEditing(null);
  };

  if (error)
    return (
      <FormPageShell>
        <div className="alert alert-danger rounded-xl border border-red-200">
          <p className="text-red-800">{t('common:loadError')}</p>
        </div>
      </FormPageShell>
    );

  return (
    <FormPageShell>
      <div className="space-y-6">
        <PageHeader
          title={t("cargosTitle")}
          description="Cargos institucionais associados a servidores e organograma."
          breadcrumbs={[
            { label: 'Início', to: getAppHomePath() },
            { label: 'Instituição', to: '/institution/job-titles' },
            { label: 'Cargos' },
          ]}
        >
          <Can permission={P.INSTITUTION_ACCESS}>
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setShowModal(true);
              }}
              className="btn btn-primary inline-flex items-center gap-2 shadow-sm"
            >
              <Plus className="h-4 w-4" /> Novo cargo
            </button>
          </Can>
        </PageHeader>
      <div className="data-list-card p-5 sm:p-6">
        <div className="mb-4">
          <input type="text" placeholder={t('common:searchPlaceholder')} value={filters.search || ''} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className="form-input max-w-xs" />
        </div>
        {isLoading ? <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div> : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50"><tr><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th><th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th></tr></thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filtered.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{c.name}</td>
                    <td className="px-6 py-4 text-right">
                      <Can permission={P.INSTITUTION_ACCESS}>
                        <button type="button" onClick={() => { setEditing(c); setShowModal(true); }} className="text-primary-600 hover:text-primary-900 p-1 mr-2"><Edit className="w-4 h-4 inline" /></button>
                        <button type="button" onClick={() => window.confirm('Excluir?') && deleteMutation.mutate(c.id)} className="text-red-600 hover:text-red-900 p-1"><Trash2 className="w-4 h-4 inline" /></button>
                      </Can>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <EmptyState
                icon={BadgeCheck}
                title={t("cargosEmpty")}
                description="Defina cargos para associar a servidores e organograma."
                action={
                  <Can permission={P.INSTITUTION_ACCESS}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        setEditing(null);
                        setShowModal(true);
                      }}
                    >
                      Novo cargo
                    </button>
                  </Can>
                }
              />
            )}
          </div>
        )}
      </div>
      </div>
      {showModal && (
        <FormModalLayout
          title={editing ? 'Editar cargo institucional' : 'Novo cargo institucional'}
          subtitle="Cargos são associados a servidores no vínculo com setor."
          headerIcon={BadgeCheck}
          historyEntityType="job_titles"
          historyEntityId={editing?.id}
          onClose={() => {
            setShowModal(false);
            setEditing(null);
          }}
          size="md"
          footer={
            <>
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setEditing(null);
                }}
                className="btn btn-outline"
              >
                Cancelar
              </button>
              <Can permission={P.INSTITUTION_ACCESS}>
                <button type="submit" form="inst-cargo-form" className="btn btn-primary">
                  Salvar
                </button>
              </Can>
            </>
          }
        >
          <form
            id="inst-cargo-form"
            key={editing?.id ?? 'new'}
            onSubmit={(e) => {
              e.preventDefault();
              const n = e.currentTarget.querySelector('input[name="cargoName"]');
              handleSubmit({ name: n?.value || '' });
            }}
            className="form-stack"
          >
            <div className="form-group">
              <label htmlFor="inst-cargo-name" className="form-label form-label-required">
                Nome do cargo
              </label>
              <input
                id="inst-cargo-name"
                name="cargoName"
                defaultValue={editing?.name}
                placeholder="Nome do cargo"
                className="form-input"
                required
              />
            </div>
          </form>
        </FormModalLayout>
      )}
    </FormPageShell>
  );
};

export default Cargos;
